import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Add, CheckCircle, FileUpload, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";

import BaseMap from "fond/map/BaseMap";
import BaseMapButtons from "fond/map/BaseMapButtons";
import BaseMapStyleToggle from "fond/map/BaseMapStyleToggle";
import { MapContext } from "fond/map/MapProvider";
import { BBox } from "fond/types/geometry";
import { AccordionStepper, AccordionStepperPane, Dropzone, NonIdealState } from "fond/widgets";
import { DropzoneHandle } from "fond/widgets/Dropzone";
import { maxFileSize } from "fond/widgets/Dropzone/utils";

import GCPSidebar from "./steps/GCPSidebar";
import ImageNameDialog from "./ImageNameDialog";
import ImageViewer from "./ImageViewer";
import MapGCPHandler from "./MapGCPHandler";
import MapImageLayer from "./MapImageLayer";
import { useImageOverlay } from "./useImageOverlay";

import {
  CenteredOverlay,
  ContentArea,
  ExplainerContent,
  FooterActions,
  MainPanel,
  MapContainer,
  MapPlaceIndicator,
  SidebarPanel,
  WizardRoot,
} from "./ImageOverlayWizard.styles";

const ACCEPTED_IMAGE_FILES = { "image/*": [".png", ".jpg", ".jpeg"] };
const MAX_IMAGE_OVERLAY_SIZE_MB = 20;
const validateFileSize = maxFileSize(MAX_IMAGE_OVERLAY_SIZE_MB);

interface ImageOverlayWizardProps {
  bbox?: BBox | null;
  onClose: () => void;
  onConfirm: (data: {
    imageName: string;
    imageUrl: string;
    imageCorners: [[number, number], [number, number], [number, number], [number, number]];
    opacity: number;
  }) => void | Promise<void>;
}

const ImageOverlayWizard: React.FC<ImageOverlayWizardProps> = ({ bbox, onClose, onConfirm }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { map, setMapStyle } = useContext(MapContext);
  const {
    imageName,
    imageUrl,
    imageCorners,
    opacity,
    isVisible,
    completeGcpCount,
    transformMatrix,
    residualError,
    mode,
    loadImage,
    addGcp,
    setOpacity,
    setVisible,
  } = useImageOverlay();

  const dropzoneRef = useRef<DropzoneHandle>(null);

  const handleDrop = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        loadImage(files[0]);
      }
    },
    [loadImage]
  );

  // Trigger map resize when step changes (container width may change)
  useEffect(() => {
    // Allow the DOM to reflow before resizing
    const frame = requestAnimationFrame(() => {
      map?.resize();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeStep, map]);

  // Advance to GCP step when a new image is loaded
  const prevImageUrl = useRef(imageUrl);
  useEffect(() => {
    if (imageUrl && imageUrl !== prevImageUrl.current) {
      setActiveStep(1);
    }
    prevImageUrl.current = imageUrl;
  }, [imageUrl]);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, 2));
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const [showImageNameDialog, setShowImageNameDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleApplyClick = useCallback(() => {
    setShowImageNameDialog(true);
  }, []);

  const handleImageNameSubmit = useCallback(
    async (name: string) => {
      if (imageUrl && imageCorners) {
        setIsSaving(true);
        try {
          await onConfirm({ imageName: name, imageUrl, imageCorners, opacity });
          setShowImageNameDialog(false);
          onClose();
        } catch {
          // Error notification and cleanup are handled by the onConfirm caller.
          // Close the name dialog so the user returns to the wizard and can retry.
          setShowImageNameDialog(false);
        } finally {
          setIsSaving(false);
        }
      }
    },
    [imageUrl, imageCorners, opacity, onConfirm, onClose]
  );

  const handleOpacityChange = useCallback(
    (_: Event, value: number | number[]) => {
      setOpacity((value as number) / 100);
    },
    [setOpacity]
  );

  const canAdvanceToConfirm = completeGcpCount >= 3 && transformMatrix !== null;

  const panes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Upload Image",
        id: "upload",
        isComplete: imageUrl !== null,
        content: (
          <Dropzone ref={dropzoneRef} onDropAccepted={handleDrop} acceptedFiles={ACCEPTED_IMAGE_FILES} multiple={false} validator={validateFileSize}>
            <NonIdealState
              icon={<FileUpload />}
              title="Upload Image"
              description="Drop an image here or click to browse. Supported formats: PNG, JPEG."
              size="small"
            />
          </Dropzone>
        ),
      },
      {
        header: "Define Ground Control Points",
        id: "gcps",
        isComplete: canAdvanceToConfirm,
        content: <GCPSidebar />,
      },
      {
        header: "Confirm",
        id: "confirm",
        isComplete: false,
        content: (
          <Box sx={{ pr: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Review the image overlay on the map. Adjust opacity as needed, then click Apply to confirm.
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={isVisible ? "Hide image" : "Show image"}>
                <IconButton size="small" onClick={() => setVisible(!isVisible)}>
                  {isVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 48 }}>
                Opacity
              </Typography>
              <Slider
                size="small"
                value={opacity * 100}
                onChange={handleOpacityChange}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        ),
      },
    ],
    [imageUrl, canAdvanceToConfirm, isVisible, opacity, handleDrop, handleOpacityChange, setVisible]
  );

  return (
    <WizardRoot>
      <ContentArea>
        <SidebarPanel>
          <AccordionStepper
            id="image-overlay"
            panes={panes}
            autoMove={false}
            allowCollapse={false}
            activeIndex={activeStep}
            onActiveIndexChange={(index) => setActiveStep(index ?? 0)}
          />
        </SidebarPanel>

        <MainPanel>
          {activeStep === 0 && !imageUrl && (
            <ExplainerContent>
              <Typography variant="h5" gutterBottom>
                Image Overlay
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={2} textAlign="center">
                Position a raster image (such as a scanned map, aerial photo, or design plan) on the map using ground control points so it can be
                displayed as an overlay.
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1} textAlign="center">
                <strong>How it works:</strong>
              </Typography>
              <Box component="ol" sx={{ color: "text.secondary", typography: "body2", pl: 2, mb: 3 }}>
                <li>Upload an image file (PNG or JPEG).</li>
                <li>Place at least 3 Ground Control Points (GCPs) by clicking matching locations on both the image and the map.</li>
                <li>Review the result and adjust opacity before applying.</li>
              </Box>
              <Button variant="contained" startIcon={<FileUpload />} onClick={() => dropzoneRef.current?.open()}>
                Upload Image
              </Button>
            </ExplainerContent>
          )}

          {activeStep === 0 && imageUrl && (
            <Box sx={{ flex: 1, display: "flex" }}>
              <ImageViewer />
            </Box>
          )}

          {imageUrl && activeStep >= 1 && (
            <>
              {activeStep === 1 && mode === "idle" && (
                <CenteredOverlay>
                  <Button variant="contained" startIcon={<Add />} onClick={addGcp} size="small">
                    Add Ground Control Point
                  </Button>
                </CenteredOverlay>
              )}

              {activeStep === 1 && (
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", position: "relative" }}>
                  <ImageViewer />
                </Box>
              )}

              <MapContainer>
                <BaseMap bbox={bbox ?? null} editMode="none" systemOfMeasurement="metric">
                  <MapGCPHandler />
                  {activeStep === 2 && <MapImageLayer />}
                  <Box className="left-sidebar-section">
                    <BaseMapStyleToggle onChange={setMapStyle} />
                  </Box>
                  <Box className="right-sidebar-section">
                    <div />
                    <BaseMapButtons
                      editMode="none"
                      onClickZoomIn={() => map?.zoomIn()}
                      onClickZoomOut={() => map?.zoomOut()}
                      currentLocationButton={false}
                    />
                  </Box>
                </BaseMap>
                {mode === "place-map" && <MapPlaceIndicator>Click on the map to place point</MapPlaceIndicator>}
              </MapContainer>
            </>
          )}
        </MainPanel>
      </ContentArea>

      {/* Footer action buttons */}
      <Divider />
      <FooterActions>
        <Stack direction="row" justifyContent="space-between">
          <Button variant="text" onClick={activeStep === 0 ? onClose : handleBack}>
            {activeStep === 0 ? "Cancel" : "Back"}
          </Button>
          {activeStep === 1 && (
            <Button variant="contained" onClick={handleNext} disabled={!canAdvanceToConfirm}>
              Next
            </Button>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleApplyClick} startIcon={<CheckCircle />}>
              Apply
            </Button>
          )}
        </Stack>
      </FooterActions>

      {showImageNameDialog && (
        <ImageNameDialog defaultName={imageName} onClose={() => setShowImageNameDialog(false)} onSubmit={handleImageNameSubmit} isSaving={isSaving} />
      )}
    </WizardRoot>
  );
};

export default ImageOverlayWizard;
